import { faCircleMinus } from "@fortawesome/pro-solid-svg-icons/faCircleMinus";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, CardContent, Grid, Pagination, Stack, Typography } from '@mui/material';
import { MprcButton, MprcCardActions, MprcContainer, MprcIconButtonMoreOptionsDropdown, MprcJoinComponents, MprcLink, MprcRequestIndicator, MprcText } from 'components';
import { MprcFilterCount } from 'components/filter';
import { CalendarDay, MprcCalendarPicker } from "components/MprcCalendarPicker";
import { MprcDropdownItem } from "components/MprcIconButtonDropdown";
import { MprcListItemTitle } from "components/MprcListItemTitle";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { AppointmentDayDto, AppointmentDto, AppointmentDtoPaginatedList, AppointmentMeetingDto, MeetingType, TrainingContentType } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { LmsTrainingDefaultThumbnailUrl } from "views/lms/components/LmsTypeImage";
import { CreateOrUpdateAppointmentDialog } from "views/lms/instructor/components/CreateOrUpdateAppointmentDialog";
import { MeetingViewer } from "views/lms/meeting/views/MeetingViewer";

////////////////////////////////////////////////////////////////////////////

export type AppointmentBaseOption = "editAppointment" | "deleteAppointment";

export interface AppointmentBaseFilter {
    meetingDate: Date | null;
    onlySpecificDay: boolean;
}

interface IProps {
    instructorId: string;
    appointmentDays: AppointmentDayDto[];
    data: AppointmentDtoPaginatedList;
    filter: AppointmentBaseFilter;
    isFiltered: boolean;
    isLoading: boolean;
    canCreateBlockAppointments: boolean;
    onGetMenuOptions: (item: AppointmentDto) => MprcDropdownItem<any>[];
    onSelectMenuOption: (option: any, item: AppointmentDto) => void;
    onChangePageNumber: (pageNumber: number) => void;
    onFilter: (data: Partial<AppointmentBaseFilter>) => void;
    onReload: () => void;
    onResetFilters: () => void;
}

////////////////////////////////////////////////////////////////////////////

export const AppointmentsCalendarList: React.FC<IProps> = (props) => {

    const { instructorId, filter, isFiltered, isLoading, canCreateBlockAppointments, appointmentDays, data, onGetMenuOptions, onSelectMenuOption, onReload, onFilter, onResetFilters, onChangePageNumber } = props;

    const api = useCoreApi();
    const { t } = useTranslation("lms");

    const [dialog, setDialog] = useState<any>();
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const dayFiltered = filter.onlySpecificDay == true;

    ////////////////////////////////////////////////////////////////////////////

    ///
    const handleCreateOrUpdateAppointment = (id: string | null) => {
        setDialog(
            <CreateOrUpdateAppointmentDialog
                instructorId={instructorId}
                appointmentId={id}
                onClose={(success) => {
                    if (success) {
                        onReload();
                    }

                    setDialog(null);
                }}
            />
        );
    };

    ///
    const handleSelectItem = (item: AppointmentDto) => {
        if (item.meeting) {
            setDialog(
                <MeetingViewer
                    componentId={item.meeting.componentId}
                    classroomId={item.meeting.classroomId}
                    meetingType={item.meeting.meetingType}
                    open={true}
                    onClose={() => setDialog(null)}
                />
            );
        }
        else {
            handleCreateOrUpdateAppointment(item.appointmentId);
        }
    };

    ///
    const resetDayFilter = () => {
        onFilter({ meetingDate: null, onlySpecificDay: false });
    };

    ///
    const buildMeetingLocation = (meeting: AppointmentMeetingDto) => {
        return meeting.meetingType == MeetingType.Presential
            ? meeting.locationName
            : meeting.meetingType == MeetingType.Other
                ? t("Instructor.List.Meetings.OnlineText")
                : `${t("Instructor.List.Meetings.OnlineAtText")} ${meeting.meetingTypeName}`;
    };

    ///
    const buildMeetingTime = (item: AppointmentDto) => {
        return (
            <MprcText color={!item.hasConflict && item.occursNext48Hours ? "warning" : "inherit"}>
                {Formaters.performaFullDateRange(item.startDate, item.endDate, { replaceClosestDays: item.meeting != null })}
                {item.meeting && item.meeting.bookedAdditionalTime &&
                    ` (${t("Instructor.List.Meetings.BookedPeriodText",
                        {
                            from: Formaters.performaFullDateRange(item.meeting.startDateBooked!, item.meeting.endDateBooked!, { replaceClosestDays: false })
                        })}) `
                }
            </MprcText>
        );
    };

    ///
    const onSelectMenuOptionBase = (option: any, item: AppointmentDto) => {
        switch (option) {
            case "editAppointment":
                handleCreateOrUpdateAppointment(item.appointmentId);
                break;
            case "deleteAppointment":
                setDeletingId(item.appointmentId);
                break;
            default:
                onSelectMenuOption(option, item);
                break;
        }
    };

    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        async function postData() {
            try {
                if (deletingId) {
                    var result = await api.v1.instructorManagement.deleteAppointment({ appointmentId: deletingId, instructorId });
                    if (result) {
                        enqueueSnackbar(t("Instructor.List.Meetings.DeleteSuccessMessage"), { variant: "success" });
                        onReload();
                    }
                    else {
                        enqueueSnackbar(t("Instructor.List.Meetings.DeleteErrorMessage"), { variant: "error" });
                    }
                }
            }
            catch (e) {
                MprcRequestSnackbarErrorReport(e);
            }
            finally {
                setDeletingId(null);
            }
        }

        postData();

    }, [deletingId]);

    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {dialog}
            <MprcRequestIndicator visible={deletingId != null} type={"post"} />
            <CardContent>
                <Grid container direction="row" spacing={4}>
                    <Grid>
                        <Stack direction="column" alignItems="center">
                            <MprcCalendarPicker
                                disableHighlightToday
                                selectedDate={filter.onlySpecificDay == true ? filter.meetingDate : null}
                                enabledDates={appointmentDays.map(p => ({ day: p.day, state: p.isConflicted ? "conflited" : p.isBlocked ? "blocked" : "busy" } as CalendarDay))}
                                onMonthChange={(month) => onFilter({ meetingDate: moment(month).toDate(), onlySpecificDay: false })}
                                onChange={(date) => onFilter({ meetingDate: date != null ? moment(date).toDate() : null, onlySpecificDay: date != null })}
                            />
                            {canCreateBlockAppointments &&
                                <MprcButton
                                    variant="text"
                                    color="error"
                                    text={t("Instructor.List.Meetings.AddAppointmentButtonText")}
                                    onClick={() => handleCreateOrUpdateAppointment(null)}
                                />
                            }
                        </Stack>
                    </Grid>
                    <Grid size="grow">
                        <MprcContainer>
                            <MprcFilterCount
                                loading={isLoading}
                                isEmpty={data.totalRows == 0}
                                countText={t("Instructor.List.Meetings.RecordsFoundText", { count: data.totalRows })}
                                filter={
                                    {
                                        active: isFiltered || dayFiltered,
                                        text: dayFiltered
                                            ? t("Instructor.List.Meetings.DayRecordsFilteredText", { count: data.totalRows, date: Formaters.dayMonth(filter.meetingDate!) })
                                            : t("Instructor.List.Meetings.RecordsFilteredText", { count: data.totalRows }),
                                        onResetFilter: dayFiltered ? resetDayFilter : onResetFilters
                                    }
                                }
                            />
                        </MprcContainer>
                        {
                            data.totalRows > 0 && (
                                <>
                                    <MprcContainer>
                                        <Grid container justifyContent="center" alignItems="center" spacing={2}>
                                            {data.items.map((item, index) => {
                                                const menuOptions = onGetMenuOptions(item);
                                                return (
                                                    <Grid key={index} alignItems="center" justifyContent="center" size={{ xs: 12 }}>
                                                        <Grid container justifyContent="flex-start" alignItems="center" spacing={1} wrap="nowrap">
                                                            <Grid>
                                                                <MprcLink onClick={() => handleSelectItem(item)}>
                                                                    <Avatar
                                                                        variant="rounded"
                                                                        sx={{ width: 70, height: 48 }}
                                                                        src={item.meeting != null ? LmsTrainingDefaultThumbnailUrl(TrainingContentType.LiveMix, item.meeting.thumbnailUrl) : ""}
                                                                    >
                                                                        {!item.meeting && <FontAwesomeIcon icon={faCircleMinus} fontSize={30} />}
                                                                    </Avatar>
                                                                </MprcLink>
                                                            </Grid>
                                                            <Grid size="grow">
                                                                <Grid container direction="row">
                                                                    <Grid size={{ xs: 12 }}>
                                                                        <Grid container alignItems="center" wrap="nowrap">
                                                                            <Grid>
                                                                                <MprcListItemTitle onClick={() => handleSelectItem(item)}>
                                                                                    {item.subject}
                                                                                </MprcListItemTitle>
                                                                            </Grid>
                                                                            <Grid>
                                                                                {menuOptions.length > 0 &&
                                                                                    <MprcIconButtonMoreOptionsDropdown
                                                                                        color="muted"
                                                                                        onChange={(selection) => onSelectMenuOptionBase(selection, item)}
                                                                                        items={menuOptions}
                                                                                    />
                                                                                }
                                                                            </Grid>
                                                                        </Grid>
                                                                    </Grid>
                                                                    {item.meeting &&
                                                                        <Grid size={{ xs: 12 }} sx={(theme) => (menuOptions.length > 0 ? { mt: theme.spacing(-.5) } : {})}>
                                                                            <Typography variant="caption">
                                                                                <MprcJoinComponents
                                                                                    components={[
                                                                                        item.meeting.trainingName,
                                                                                        item.meeting.multipleClasses ? item.meeting.classroomName : null
                                                                                    ]}
                                                                                    separator="&bull;"
                                                                                />
                                                                            </Typography>
                                                                        </Grid>
                                                                    }
                                                                    <Grid size={{ xs: 12 }} sx={(theme) => (menuOptions.length > 0 ? { mt: theme.spacing(-.5) } : {})}>

                                                                        {item.meeting?.isCancelled == true
                                                                            ? <Typography variant="captionHeader" color="error">
                                                                                {t("Student.EnrollmentView.Components.CanceledText")}
                                                                            </Typography>
                                                                            : <Typography variant="caption" color={item.hasConflict ? "error" : "inherit"}>
                                                                                <MprcJoinComponents
                                                                                    components={[
                                                                                        item.startDate != null ? buildMeetingTime(item) : null,
                                                                                        item.meeting ? buildMeetingLocation(item.meeting) : null
                                                                                    ]}
                                                                                    separator="&bull;"
                                                                                />
                                                                            </Typography>
                                                                        }

                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                );
                                            })
                                            }
                                        </Grid>
                                    </MprcContainer>
                                    <MprcCardActions>
                                        {data && data.totalPages > 1 && (
                                            <Pagination
                                                shape="rounded"
                                                color="primary"
                                                page={data.pageNumber}
                                                count={data.totalPages}
                                                onChange={(_, pgNumber) => onChangePageNumber(pgNumber)}
                                            />
                                        )}
                                    </MprcCardActions>
                                </>
                            )
                        }
                    </Grid>
                </Grid>

            </CardContent>
        </>
    );
};
